import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { Menu, X } from "lucide-react";

export const Navbar = () => {
  const { locale, setLocale, t } = useLang();
  const [open, setOpen] = React.useState(false);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/genese", label: t("nav.genese") },
    { to: "/geobiologie", label: t("nav.geobiologie") },
    { to: "/cuisine-vivante", label: t("nav.cuisine") },
    { to: "/magnetisme", label: t("nav.magnetisme") },
    { to: "/sentinelles-vertes", label: t("nav.sentinelles") },
    { to: "/bilan", label: t("nav.bilan") },
    { to: "/frequences", label: t("nav.frequences") },
    { to: "/tarifs", label: t("nav.tarifs") },
    { to: "/devis", label: t("nav.devis") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const soonLabel = locale === "fr" ? "Bientôt" : "Soon";

  return (
    <header
      className="sticky top-0 z-40 glass border-b border-[#1E293B]"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo-link">
          <span className="font-display text-xl tracking-[0.25em] text-amber-glow">FACOW360</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => {
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative inline-flex items-center gap-1.5 text-sm font-mono-tech tracking-widest uppercase transition-colors ${
                    isActive ? "text-cyan-glow" : "text-slate-300 hover:text-[#00F0FF]"
                  }`
                }
                data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              >
                {l.to === "/genese" ? (
                  <span className="text-center leading-snug">
                    <span className="block whitespace-nowrap">{locale === "fr" ? "D'où vient" : "Where does"}</span>
                    <span className="block whitespace-nowrap">{locale === "fr" ? "FACOW 360 ?" : "FACOW 360 come from?"}</span>
                  </span>
                ) : (
                  <span>{l.label}</span>
                )}
                {l.comingSoon && (
                  <span
                    className="px-1.5 py-0.5 rounded-sm text-[8px] font-mono-tech tracking-[0.18em] border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] leading-none"
                    data-testid={`nav-soon-${l.to.replace("/", "")}`}
                  >
                    {soonLabel}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 text-xs font-mono-tech">
            <button
              onClick={() => setLocale("fr")}
              className={`px-2 py-1 rounded ${locale === "fr" ? "text-[#D4AF37]" : "text-slate-500"}`}
              data-testid="lang-switch-fr"
            >FR</button>
            <span className="text-slate-700">|</span>
            <button
              onClick={() => setLocale("en")}
              className={`px-2 py-1 rounded ${locale === "en" ? "text-[#D4AF37]" : "text-slate-500"}`}
              data-testid="lang-switch-en"
            >EN</button>
          </div>
          <button
            className="lg:hidden text-slate-200"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-[#1E293B]" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => {
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 text-sm font-mono-tech tracking-widest uppercase ${
                      isActive ? "text-cyan-glow" : "text-slate-300"
                    }`
                  }
                  data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                >
                  <span>{l.label}</span>
                  {l.comingSoon && (
                    <span className="px-1.5 py-0.5 rounded-sm text-[8px] font-mono-tech tracking-[0.18em] border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] leading-none">
                      {soonLabel}
                    </span>
                  )}
                </NavLink>
              );
            })}
            <div className="flex items-center gap-2 pt-2">
              <button onClick={() => setLocale("fr")} className={`px-2 py-1 text-xs ${locale === "fr" ? "text-[#D4AF37]" : "text-slate-500"}`}>FR</button>
              <span className="text-slate-700">|</span>
              <button onClick={() => setLocale("en")} className={`px-2 py-1 text-xs ${locale === "en" ? "text-[#D4AF37]" : "text-slate-500"}`}>EN</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
